import React, { useState } from 'react';
import { Form, FormGroup, Input, Label, ModalBody } from 'reactstrap';
import styled from 'styled-components';
import ApiUser from '../../api/api-user';
import UriHelper from '../../lib/uri-helper';
import Analytics from '../../lib/user-analytics';
import UserHelper from '../../lib/user-helper';
import { SubmittingSpinner } from '../Shared/submitting-spinner';
import { passwordActiveUrl, passwordInActiveUrl } from './registration-icons';

interface PasswordResetProps {
    onClose: () => void;
}

export const PasswordReset = (props: PasswordResetProps) => {
    const id = UriHelper.tryGetParam('id') || '';
    const email = UriHelper.tryGetParam('email') || '';

    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

    const handleSubmitResetPassword = (e) => {
        e.preventDefault();

        if (!UserHelper.isEmailValid(email)) {
            setError('Something has gone wrong, please open from the email link sent to you');
            return;
        }

        if (!password && !confirmPassword) {
            setError('Please enter a password and confirmation password');
            return;
        }

        if (password !== confirmPassword) {
            setError('Please make sure the password and confirm password are the same.');
            setConfirmPassword('');
            return;
        }

        if (password.length < 6) {
            setError('Please ensure the password is 6 or more characters long');
            setPassword('');
            setConfirmPassword('');
            return;
        }

        if (!error && password && email && id) {
            Analytics.Event('Registration', 'Reset password');
            setIsSubmitting(true);
            ApiUser.resetPasswordPromise(id, email, password)
                .then(() => {
                    setIsSubmitting(false);
                    setSubmitted(true);
                    setTimeout(() => {
                        setSubmitted(false);
                        props.onClose();
                    }, 3000);
                })
                .catch((error) => {
                    if (error.toString().includes('400')) {
                        setError(
                            'It looks like you may have used this reset token or something has gone wrong, please request another reset email'
                        );
                    } else {
                        setError(error.toString().substring(11, error.length));
                    }
                    setIsSubmitting(false);
                });
        }
    };

    return (
        <React.Fragment>
            {!submitted ? (
                <>
                    <ResetModalBody>
                        <Form onSubmit={(e) => handleSubmitResetPassword(e)}>
                            <FormGroup>
                                <Label for="password">Please enter a new password and confirm</Label>
                                <Input
                                    data-sentry-block
                                    data-testid="password"
                                    style={{ marginTop: '20px' }}
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setError('');
                                    }}
                                />
                                <ResetPasswordIcon
                                    onClick={() => setShowPassword(!showPassword)}
                                    showPasswordIcon={showPassword}
                                />
                                <Input
                                    data-testid="confirm-password"
                                    style={{ marginTop: '20px' }}
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder="Confirm password"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        setError('');
                                    }}
                                />
                                <ResetPasswordIcon
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    showPasswordIcon={showConfirmPassword}
                                />
                            </FormGroup>
                            {error && (
                                <ResetModalError>
                                    <ResetModalErrorIcon />
                                    {error}
                                </ResetModalError>
                            )}
                            {!isSubmitting ? (
                                <ResetSubmitButton
                                    onSubmit={(e) => handleSubmitResetPassword(e)}
                                    error={error ? true : false}
                                >
                                    SUBMIT
                                </ResetSubmitButton>
                            ) : (
                                <SubmittingSpinner />
                            )}
                        </Form>
                    </ResetModalBody>
                </>
            ) : (
                <>
                    <ResetModalBody>
                        <h2>Your password has been reset</h2>
                        <p>Redirecting to login</p>
                    </ResetModalBody>
                </>
            )}
        </React.Fragment>
    );
};

const ResetModalError = styled.div`
    color: red !important;
    -webkit-text-fill-color: red;
    padding: 10px;
    padding-top: 0px;
    display: flex;
`;

const ResetModalErrorIcon = styled.div`
    background-image: url("data:image/svg+xml,%3Csvg width='15' height='15' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 17V29' stroke='%23FF0000' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M20 11V13' stroke='%23FF0000' stroke-linecap='round' stroke-linejoin='round'/%3E%3Ccircle cx='20' cy='20' r='19.5' stroke='%23FF0000'/%3E%3C/svg%3E ");
    background-position: center;
    background-repeat: no-repeat;
    width: 20px;
    height: 20px;
    margin-right: 10px;
`;

const ResetModalBody = styled(ModalBody)`
    label {
        color: rgba(255, 255, 255, 0.87);
        width: 100%;
    }

    input {
        background-color: transparent;
        color: rgba(255, 255, 255, 0.6) !important;
        border: 1px solid rgb(255 255 255 / 0.3) !important;
        -webkit-text-fill-color: rgba(255, 255, 255, 0.6) !important;

        :-webkit-autofill,
        :-webkit-autofill:hover,
        :-webkit-autofill:focus,
        :-webkit-autofill:active,
        :focus {
            background-color: transparent;
            border-color: rgba(238 227 8 / 0.6);
            box-shadow: 0 0 0 0.2rem rgba(238 227 8 / 0.1);
            color: rgba(255, 255, 255, 0.6) !important;
            -webkit-text-fill-color: rgba(255, 255, 255, 0.6) !important;
        }
    }

    h2 {
        color: #eed926;
    }

    p {
        color: rgba(255, 255, 255, 0.87);
        text-align: center;
        margin: 25px 0px;
    }
`;

interface PasswordIconProps {
    showPasswordIcon: boolean;
}
const ResetPasswordIcon = styled.div<PasswordIconProps>`
    background-image: url('${(props) => (props.showPasswordIcon ? passwordActiveUrl : passwordInActiveUrl)}');
    background-repeat: no-repeat;
    width: 25px;
    height: 20px;
    float: right;
    margin-top: -26px;
    margin-right: 5px;
`;

interface ErrorProps {
    error: boolean;
}

const ResetSubmitButton = styled.button<ErrorProps>`
    margin: 0 auto;
    display: block;
    margin-top: ${(props) => (props.error ? '0px' : '20px')};
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 25px;
    padding-right: 25px;
    font-size: 1rem;
    border: 1px solid rgba(0, 0, 0, 0.2);
    color: black !important;
    border-radius: 4px;
    background-color: gray;
    width: -webkit-fill-available;

    :not(:disabled) {
        cursor: pointer;
        background-color: #eed926 !important;
        -webkit-text-fill-color: black;
    }
`;
